import { useState, useEffect, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import type { ProjectProfile } from '@/lib/types'

// Loads ALL public project profiles for the registry / detail pages.
export function useAllProjects() {
  const [projects, setProjects] = useState<ProjectProfile[]>([])
  const [loading, setLoading] = useState(true)

  const fetchProjects = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.project_profiles.list({
        sort: { createdAt: -1 },
      })
      setProjects((list ?? []) as ProjectProfile[])
    } catch (error) {
      console.error('Failed to load projects:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  return { projects, loading, refetch: fetchProjects }
}

// Loads the SINGLE profile owned by the currently authenticated user.
// Enforces "1 login = 1 account": we always operate on the first record
// whose creator matches the user's id.
export function useMyProject(userId: string | undefined) {
  const [profile, setProfile] = useState<ProjectProfile | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchMine = useCallback(async () => {
    if (!userId) {
      setProfile(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { list } = await lumi.entities.project_profiles.list({
        filter: { creator: userId },
        sort: { createdAt: 1 },
        limit: 1,
      })
      setProfile(((list ?? [])[0] as ProjectProfile) ?? null)
    } catch (error) {
      console.error('Failed to load your project:', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchMine()
  }, [fetchMine])

  return { profile, loading, refetch: fetchMine }
}
